import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useSidebarState = (handleNavigationAttempt, dynamicSidebarCategories, helpSupportList) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Initialize expanded categories on first load
  useEffect(() => {
    const currentPath = location.pathname;
    const initialExpanded = new Set();
    
    if (dynamicSidebarCategories) {
      dynamicSidebarCategories.forEach(category => {
        if (category.items && Array.isArray(category.items)) {
          const hasMatchingItem = category.items.some(item => item.link === currentPath);
          if (hasMatchingItem && category.items.length > 1) {
            initialExpanded.add(category.name);
          }
        }
      });
      
      if (initialExpanded.size > 0) {
        setExpandedCategories(initialExpanded);
      }
    }
  }, [location.pathname, dynamicSidebarCategories]);

  // Set selected item based on current path
  useEffect(() => {
    if (dynamicSidebarCategories) {
      let allNavigationItems = [];
      
      dynamicSidebarCategories.forEach(category => {
        if (category.items && Array.isArray(category.items)) {
          allNavigationItems.push(...category.items);
        } else {
          allNavigationItems.push(category);
        }
      });
      
      if (helpSupportList) {
        allNavigationItems.push(...helpSupportList);
      }
      
      const defaultItem = allNavigationItems.find((item) => item.link === location.pathname);
      if (defaultItem && defaultItem.name !== selectedItem) {
        setSelectedItem(defaultItem.name);
      }
    }
  }, [location.pathname, selectedItem, dynamicSidebarCategories, helpSupportList]);


  const toggleCategory = useCallback((categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(categoryName) ? newSet.delete(categoryName) : newSet.add(categoryName);
      return newSet;
    });
  }, []);

  const handleNavigation = useCallback((item) => {
    if (selectedItem !== item.name) {
      setSelectedItem(item.name);
    }
    setMobileOpen(false);
    
    const targetLink = item.link;
    if (item.name === "Community") {
      navigate(targetLink, { state: { reset: Date.now() } });
    } else if (handleNavigationAttempt) {
      handleNavigationAttempt(targetLink);
    } else {
      navigate(targetLink);
    }
  }, [selectedItem, navigate, handleNavigationAttempt, setMobileOpen]);

  const handleCategoryClick = useCallback((category) => {
    if (category.items) {
      category.items.length === 1 
        ? handleNavigation(category.items[0])
        : toggleCategory(category.name);
    } else {
      handleNavigation(category);
    }
  }, [toggleCategory, handleNavigation]);

  const toggleMobileOpen = useCallback(() => setMobileOpen(!mobileOpen), [mobileOpen]);
  const toggleDesktopOpen = useCallback(() => setDesktopOpen(!desktopOpen), [desktopOpen]);

  return {
    mobileOpen,
    desktopOpen,
    selectedItem,
    expandedCategories,
    toggleCategory,
    handleCategoryClick,
    handleNavigation,
    toggleMobileOpen,
    toggleDesktopOpen,
    setMobileOpen,
    location
  };
}; 